import { useMemo } from "react";

import { Text } from "@vericus/cadmus-ui";

import { useAppDispatch, useAppSelector } from "@/data/hooks";
import { selectAnswerSnapshot, setLocalSnapshot } from "@/features/authority";
import { SimpleChoice } from "@/generated/graphql";
import { Snapshot } from "@/stores/snapshot";

import { DropdownChoices } from "../dropdown-choices";
import * as styles from "./answer-form.css";

interface Props {
  /** Answer ID */
  answerBlockId: string;
  /** Field interaction */
  fieldIdentifier: string;
  /** List of prompts to match with */
  targetSet: SimpleChoice[];
  /** List of choices for each drop down */
  sourceSet: SimpleChoice[];
  /** Whether the options should be shuffled */
  shuffle?: boolean;
  /** Whether the input is readonly */
  isReadonly?: boolean;
}

/**
 * Answer response form for Matching with Dropdown options.
 */
export function MatchingResponseForm(props: Props) {
  const {
    answerBlockId,
    fieldIdentifier,
    isReadonly,
    targetSet,
    sourceSet,
    shuffle,
  } = props;

  const snapshot = useAppSelector((state) =>
    selectAnswerSnapshot(state, answerBlockId)
  );
  const dispatch = useAppDispatch();

  const answers = getAnswerDirectedPairs(snapshot, fieldIdentifier);
  const version = snapshot?.version ?? 0;

  // Map the user's selected answers as a key-value pair
  // of target and source identifiers
  const answersMap = useMemo(() => {
    return answers.map((pair) => {
      const [targetIdentifier, sourceIdentifier] = pair.split(" ");
      return { targetIdentifier, sourceIdentifier };
    });
  }, [answers]);

  // Callback to update a dropdown choice
  const setChoice = (targetIdentifier: string, sourceIdentifier: string) => {
    // If answered before, update the answer,
    // Else, add a new answer
    const updatedAnswer = answersMap.map((pair) => {
      if (pair.targetIdentifier === targetIdentifier) {
        return `${targetIdentifier} ${sourceIdentifier}`;
      }
      return `${pair.targetIdentifier} ${pair.sourceIdentifier}`;
    });

    if (
      !answersMap.some((pair) => pair.targetIdentifier === targetIdentifier)
    ) {
      updatedAnswer.push(`${targetIdentifier} ${sourceIdentifier}`);
    }

    const isIncomplete = updatedAnswer.length < targetSet.length;

    dispatch(
      setLocalSnapshot({
        answerBlockId,
        snapshot: {
          version: version + 1,
          answerFields: {
            ...snapshot?.answerFields,
            [fieldIdentifier]: updatedAnswer,
          },
          isIncomplete,
        },
      })
    );
  };

  return (
    <div className={styles.response}>
      <div className={styles.fieldsContainer}>
        {targetSet.map(({ identifier, content }, index) => (
          <div key={identifier} className={styles.matchingPair}>
            <div className={styles.label}>
              <Text kind="bodyMd" color="neutralBlack300">
                {`${index + 1}.`}
              </Text>
            </div>
            <div className={styles.prompt}>
              <Text kind="bodyMd" color="neutralBlack300">
                {content}
              </Text>
            </div>
            <DropdownChoices
              width={420}
              choices={sourceSet}
              seed={answerBlockId}
              shuffle={shuffle}
              onSelectChoice={(choiceId: string) => {
                setChoice(identifier, choiceId);
              }}
              selectedChoiceId={
                answersMap.find((pair) => pair.targetIdentifier === identifier)
                  ?.sourceIdentifier ?? null
              }
              isReadonly={isReadonly}
              placeholder="Select an answer"
              hasSeparator={true}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function getAnswerDirectedPairs(
  snapshot: Snapshot | undefined,
  fieldIdentifier: string
): string[] {
  if (snapshot?.answerFields) {
    return snapshot.answerFields[fieldIdentifier] ?? [];
  }
  return [];
}
